import React from "react";
import Card from "../../ui/Card";
import { useRBAC } from "../../../hooks/useRBAC";
import type { TeamMember } from "../../../types/core";

// =============================================================================
// TEAM ROSTER
// =============================================================================
// PURPOSE: Simple table of team members visible on the dashboard.
//
// VISIBILITY: Only shown to exec/manager roles (see roleConfig.ts).
//
// POD SCOPING (Phase 6):
// - exec/super-admin: sees all members (canViewAllPods)
// - manager with managedPodIds: sees only members in their pods
// - manager without managedPodIds: sees all (fallback, shouldn't occur)
//
// DISPLAYED COLUMNS:
// - Name, email, role, status
// =============================================================================

interface TeamRosterProps {
  teamMembers: TeamMember[];
}

const TeamRoster: React.FC<TeamRosterProps> = ({ teamMembers }) => {
  const { canViewAllPods, isManager, managedPodIds } = useRBAC();

  // Scope to managed pods for managers; exec sees everyone
  const visibleMembers =
    isManager && managedPodIds.length > 0 && !canViewAllPods
      ? teamMembers.filter((m) => m.pod_id && managedPodIds.includes(m.pod_id))
      : teamMembers;

  if (visibleMembers.length === 0) return null;

  return (
    <Card>
      <h3 className="text-lg font-bold text-glass mb-4">Team Roster</h3>
      <div className="overflow-x-auto -mx-6">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-black/[0.08] text-left text-xs uppercase text-glass-muted tracking-wider">
              <th className="px-6 py-3">Name</th>
              <th className="px-6 py-3">Email</th>
              <th className="px-6 py-3">Role</th>
              <th className="px-6 py-3">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-black/[0.04]">
            {visibleMembers.map((m, idx) => (
              <tr
                key={m.user_id}
                className="hover:bg-black/[0.04] transition-colors anim-pop"
                style={{
                  animationDelay: `${Math.min(idx, 15) * 100}ms`,
                  animationDuration: "480ms",
                }}
              >
                <td className="px-6 py-3 font-medium text-glass">{m.user_name}</td>
                <td className="px-6 py-3 text-glass-secondary">{m.user_email}</td>
                <td className="px-6 py-3 capitalize text-glass-secondary">{m.role}</td>
                <td className="px-6 py-3 capitalize text-glass-secondary">{m.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

export default TeamRoster;
